import { Suspense, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { BrowserRouter as Router, Switch, Route } from 'react-router-dom';
import {
  setCustomerManufacturerList,
  setCustomerSupplierList,
  setLanguageCode,
  setTranslations,
  setUserInfo,
} from 'reducers/pageAction';
import { get, getTranslations } from 'api';
import { getManufacturerListing, getSupplierListing } from 'api/get';
import i18next, { languages } from 'utils/i18n';
import { isForWorkplaceSafety } from 'utils/helper';
import { ApiResponse } from 'interface/common/ApiResponseInterface';
import { GetTranslationDto } from 'interface';
import UserInterface from 'interface/user';
import { AppDispatch } from 'const';
import { defaultRoutes, guardRoutes, RouteInterface } from './routes-data';
import { LanguageProvider } from 'pages/LanguageProvider';
import { Layout } from 'components/layout';
import AuthenticatingPopup from 'components/popups/authenticating-popup';
import CustomLoader from 'components/loader/Loader';
import PermissionRoute from './permission-route';

function Routes() {
  const dispatch: AppDispatch = useDispatch();
  const userInfo: UserInterface | null = useSelector(
    (state: any) => state.pageReducer.userInfo
  );
  const location = window.location.pathname;
  const languageCode = localStorage.getItem('languageCode');
  const translations = useSelector(
    (state: any) => state.pageReducer.translations
  );
  const [authenticating, setAuthenticating] = useState<boolean>(true);
  const isAuthenticationPage = [
    '/login/',
    'reset-password',
    'onesignin',
    '/external-login/',
  ].some(item => {
    return location.toLowerCase().indexOf(item) > -1;
  });

  const fetchTranslations = (): void => {
    getTranslations().then((response: ApiResponse<GetTranslationDto>) => {
      if (response?.status === 200) dispatch(setTranslations(response.data));
    });
  };

  const fetchUser = () => {
    setAuthenticating(true);
    if (!isAuthenticationPage) {
      const getUserRequest = get('/user/', {});
      getUserRequest.then(response => {
        if (response.status === 200) {
          if (!translations && !isAuthenticationPage) fetchTranslations();

          const userData = response.data;
          dispatch(setUserInfo(userData));
          let setLang = languageCode
            ? languageCode
            : languages.includes(userData.language_code)
            ? userData.language_code
            : 'en';
          dispatch(setLanguageCode(setLang));
          if (i18next.language !== setLang) i18next.changeLanguage(setLang);
          setAuthenticating(false);
        }
      });
    }
  };

  const fetchCustomerSupplierList = () => {
    if (!isAuthenticationPage) {
      getSupplierListing().then(response => {
        if (response.status === 200) {
          dispatch(setCustomerSupplierList(response.data));
        }
      });
    }
  };

  const fetchCustomerManufacturerList = () => {
    if (!isAuthenticationPage) {
      getManufacturerListing().then(response => {
        if (response.status === 200) {
          dispatch(setCustomerManufacturerList(response.data));
        }
      });
    }
  };

  const updateHeadElement = () => {
    const headElement = document.getElementsByTagName('head')[0];
    const linkIcon = document.createElement('link');
    const title = document.createElement('title');
    linkIcon.rel = 'icon';
    if (isForWorkplaceSafety()) {
      linkIcon.href = '/favicon-2.png';
      title.innerText = 'SDS creator';
    } else {
      linkIcon.href = '/favicon-1.png';
      title.innerText = 'SDS Authoring';
    }
    if (headElement) {
      headElement.appendChild(linkIcon);
      headElement.appendChild(title);
    }
  };

  useEffect(() => {
    updateHeadElement();
    fetchUser();
    fetchCustomerSupplierList();
    fetchCustomerManufacturerList();
  }, []);

  const page = (
    <Switch>
      {guardRoutes.map((item: RouteInterface) => {
        const { path, key, exact, RouteComponent, active, props } = item;

        if (!active) return null;

        return (
          <Route key={key ?? path} exact={exact} path={path}>
            <PermissionRoute key={key ?? path} user={userInfo}>
              <RouteComponent {...props} />
            </PermissionRoute>
          </Route>
        );
      })}

      {defaultRoutes.map((item: RouteInterface) => {
        const { path, key, exact, RouteComponent, active, props } = item;

        if (!active) return null;

        return (
          <Route key={key ?? path} exact={exact} path={path}>
            <RouteComponent {...props} />
          </Route>
        );
      })}
    </Switch>
  );

  return (
    <LanguageProvider
      language={
        languageCode
          ? languageCode
          : languages.includes(userInfo?.language_code ?? 'en')
          ? userInfo?.language_code
          : 'en'
      }
    >
      {authenticating && !isAuthenticationPage ? (
        <AuthenticatingPopup />
      ) : (
        <Router>
          <Layout user={userInfo}>
            <Suspense fallback={<CustomLoader />}>{page}</Suspense>
          </Layout>
        </Router>
      )}
    </LanguageProvider>
  );
}

export default Routes;
